import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import {
  buildCertificateUnitRows,
  formatCreditValue,
  resolveBundledCreditsTotal,
} from "./Utils/certificateUnitRowsUtils";

// Register Google Sans Flex (math subset) for reliable subscript glyphs like ₂.
Font.register({
  family: "Lexend",
  src: "https://cdn.jsdelivr.net/npm/@fontsource/lexend-deca@5.2.10/files/lexend-deca-latin-400-normal.woff",
  fontWeight: "normal",
});
Font.register({
  family: "Lexend",
  src: "https://cdn.jsdelivr.net/npm/@fontsource/lexend-deca@5.2.10/files/lexend-deca-latin-500-normal.woff",
  fontWeight: 500,
});
Font.register({
  family: "GoogleSansFlex",
  src: "https://cdn.jsdelivr.net/fontsource/fonts/google-sans-flex@latest/math-400-normal.woff",
  fontWeight: "normal",
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 57,
    paddingBottom: 85,
    paddingLeft: 85,
    paddingRight: 85,
    fontSize: 10,
    fontFamily: "Lexend",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 20,
  },
  logo: {
    height: 36,
  },
  title: {
    fontSize: 20,
    color: "#35B28D",
    marginBottom: 10,
    fontWeight: 500,
  },
  sectionTitle: {
    fontSize: 14,
    color: "#35B28D",
    marginTop: 10,
    marginBottom: 8,
    fontWeight: 500,
  },
  text: {
    marginBottom: 4,
    fontSize: 10,
  },
  bold: {
    fontWeight: 500,
  },
  table: {
    display: "flex",
    flexDirection: "column",
    marginTop: 10,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingVertical: 6,
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: 500,
    paddingVertical: 8,
  },
  tableCell: {
    flex: 1.2,
    paddingHorizontal: 4,
    fontSize: 9,
    width: "30%",
  },
  tableCellSmall: {
    flex: 0.7,
    paddingHorizontal: 4,
    fontSize: 9,
    display: "flex",
    flexDirection: "column",
    width: "14%",
  },
  tableCellId: {
    flex: 0.8,
    paddingHorizontal: 4,
    fontSize: 9,
    display: "flex",
    flexDirection: "column",
    width: "16%",
  },
  tableCellAssetId: {
    flex: 1.2,
    paddingHorizontal: 4,
    fontSize: 9,
    display: "flex",
    flexDirection: "column",
    width: "24%",
  },
  projectInfoContainer: {
    flexDirection: "row",
    marginTop: 10,
  },
  projectInfoLeft: {
    flex: 1,
    maxWidth: "50%",
    padding: 10,
  },
  projectInfoRight: {
    flex: 1,
    padding: 10,
  },
  amountHeader: {
    textAlign: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#d9d9d9",
    padding: 10,
    fontSize: 10,
    fontWeight: 500,
  },
  amountValue: {
    fontSize: 20,
    color: "#35B28D",
    textAlign: "center",
    marginTop: 35,
    fontWeight: 500,
  },
  pageBreak: {
    marginTop: 20,
  },
  subscript: {
    fontSize: 7,
    fontFamily: "GoogleSansFlex",
  },
});

function formatDateRange(dates) {
  if (!dates || dates.length < 2) return "Not specified";

  const getTimestamp = (date) => {
    if (!date) return null;
    return date?.$date ? date.$date : date;
  };

  const startTimestamp = getTimestamp(dates[0]);
  const endTimestamp = getTimestamp(dates[1]);

  if (!startTimestamp || !endTimestamp) return "Not specified";

  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  const startDate = new Date(startTimestamp).toLocaleDateString("en-US", options);
  const endDate = new Date(endTimestamp).toLocaleDateString("en-US", options);

  return `${startDate} - ${endDate}`;
}

function formatDate(date) {
  if (!date) return "-";
  const timestamp = date?.$date ? date.$date : date;
  if (!timestamp) return "-";
  const d = new Date(timestamp);
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return d.toLocaleDateString("en-US", options);
}

function formatMethodology(methodologies) {
  if (Array.isArray(methodologies) && methodologies.length > 0) {
    return methodologies.map((method, idx) => {
      if (typeof method === "object" && method?.id && method?.name) {
        return (
          <Text key={idx}>
            • {method.id}: {method.name}
          </Text>
        );
      }
      return (
        <Text key={idx}>
          • {String(method)}
        </Text>
      );
    });
  }

  if (typeof methodologies === "string") {
    return methodologies
      .split(", ")
      .map((method, idx) => <Text key={idx}>• {method}</Text>);
  }

  return "-";
}

function renderCO2e() {
  return (
    <Text>
      tCO
      <Text style={styles.subscript}>₂</Text>
      e
    </Text>
  );
}

export const RetirementCertificatePDFDocument = ({
  yieldRecordRetiredCertificateDetails,
  finalTokenData,
  certificateUnitRows: certificateUnitRowsProp,
  assetBasePath,
  bundledCreditsTotal: bundledCreditsTotalProp,
}) => {
  const certificateData = yieldRecordRetiredCertificateDetails[0] || {};
  const logoSrc = assetBasePath ? `${assetBasePath}/logo512.png` : "/logo512.png";
  const certificateUnitRows =
    certificateUnitRowsProp !== undefined && certificateUnitRowsProp !== null
      ? certificateUnitRowsProp
      : buildCertificateUnitRows(certificateData, undefined);

  const retirementDate = finalTokenData?.created
    ? formatDate(finalTokenData.created)
    : "-";

  const amountRetired = finalTokenData?.no_of_token
    ? Number(finalTokenData.no_of_token)
      .toFixed(4)
      .replace(/\.?0+$/, "")
    : "N/A";
  const bundledCredits =
    bundledCreditsTotalProp != null
      ? bundledCreditsTotalProp
      : resolveBundledCreditsTotal(certificateData, []);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logoSrc} style={styles.logo} />
        </View>

        <Text style={styles.title}>Proof of Retirement</Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>{finalTokenData?.beneficiary_name}</Text>
          {" has retired "}
          <Text style={styles.bold}>
            {amountRetired} {renderCO2e()}
          </Text>
          {" of Carbon Credits as part of the project "}
          <Text style={styles.bold}>{certificateData.intervention_name || "-"}</Text>
        </Text>

        <View style={styles.pageBreak}>
          <Text style={styles.sectionTitle}>Retirement Details</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={styles.tableCell}>Beneficiary</Text>
              <Text style={styles.tableCell}>Retirement Purpose</Text>
              <Text style={styles.tableCell}>Retirement Date</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={styles.tableCell}>
                {finalTokenData?.beneficiary_name || "-"}
              </Text>
              <Text style={styles.tableCell}>
                {finalTokenData?.certificate_retirement_purpose || "-"}
              </Text>
              <Text style={styles.tableCell}>{retirementDate}</Text>
            </View>
          </View>
        </View>

        <View style={styles.pageBreak}>
          <Text style={styles.sectionTitle} minPresenceAhead={120}>
            Certificate Details
          </Text>
          {certificateData.ghg_impact_summary && (
            <Text style={[styles.text, { marginBottom: 10 }]}>
              {certificateData.ghg_impact_summary}
            </Text>
          )}

          <Text style={styles.text}>
            <Text style={styles.bold}>Certificate bundle ID: </Text>
            {finalTokenData?.finaltoken_id
              ? `PTCB-${finalTokenData.finaltoken_id}`
              : "DRY-RUN-XXXX"}
          </Text>

          <Text style={styles.text}>
            <Text style={styles.bold}>Bundle issuance date: </Text>
            {formatDate(finalTokenData?.created || new Date())}
          </Text>

          {certificateData.cropLabel && (
            <Text style={styles.text}>
              <Text style={styles.bold}>Crop: </Text>
              {certificateData.cropLabel}
            </Text>
          )}

          {certificateData.crop_yield_emissions?.totalCropYield != null && (
            <Text style={styles.text}>
              <Text style={styles.bold}>Total crop yield: </Text>
              {Number(certificateData.crop_yield_emissions.totalCropYield).toFixed(0)} ton
            </Text>
          )}

          {bundledCredits != null && (
            <Text style={styles.text}>
              <Text style={styles.bold}>Bundled credits: </Text>
              {formatCreditValue(bundledCredits)}
            </Text>
          )}

          <Text style={styles.text}>
            <Text style={styles.bold}>Retired Amount: </Text>
            {amountRetired}
          </Text>

          {certificateUnitRows.length > 0 && (
              <View style={{ marginTop: 8 }}>
                <Text
                  style={[styles.text, styles.bold, { marginBottom: 4 }]}
                >
                  Certified Emission Reduction Units:
                </Text>
                <Text style={[styles.text, { marginBottom: 8 }]}>
                  Each unit represents a verified quantity of emission reductions and is issued as a unique digital token recorded on the blockchain.
                </Text>

                <View style={styles.table} minPresenceAhead={50}>
                  <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.tableCellId}>
                      ID
                    </Text>
                    <Text style={styles.tableCellSmall}>Token Creation Date</Text>
                    <Text style={styles.tableCellAssetId}>Asset ID</Text>
                    <Text style={styles.tableCellSmall}>Period</Text>
                    <Text style={styles.tableCellSmall}>Product Volume</Text>
                    <Text style={styles.tableCellSmall}>Token GHG Mitigation</Text>
                    <Text style={styles.tableCellSmall}>Retired Amount</Text>
                  </View>

                  {certificateUnitRows.map((row) => (
                    <View key={row.key} style={styles.tableRow}>
                      <Text style={styles.tableCellId}>
                        {row.tokenId}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.issuanceDate}
                      </Text>
                      <Text style={styles.tableCellAssetId}>
                        {row.assetId}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.period}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.productVolume}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.yield}
                      </Text>
                      <Text style={styles.tableCellSmall}>
                        {row.bundledAmount}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
        </View>

        <View style={styles.pageBreak} wrap={false}>
          <Text style={styles.sectionTitle} minPresenceAhead={200}>
            Project Details
          </Text>
          <View wrap={false}>
            <Text style={styles.text}>
              <Text style={styles.bold}>Project Name:</Text> 
              {certificateData.intervention_name || "-"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>GHG Project Summary:</Text> 
              {certificateData.intervention_summary || "-"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Project ID:</Text> 
              {certificateData.intervention_project_id || "-"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Id:</Text> PTCB-
              {finalTokenData?.finaltoken_id || "-"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>GHG Project Type:</Text> 
              {certificateData.intervention_type || "-"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Methodology:</Text> 
              {formatMethodology(certificateData.intervention_method)}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Standards Used:</Text> 
              {Array.isArray(certificateData.intervention_standards_used)
                ? certificateData.intervention_standards_used.join(", ")
                : certificateData.intervention_standards_used || "-"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Location:</Text> 
              {certificateData.intervention_region || "-"}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Crediting Period:</Text> 
              {formatDateRange(certificateData.intervention_crediting_period)}
            </Text>
            <Text style={styles.text}>
              <Text style={styles.bold}>Project Period:</Text> 
              {formatDateRange(certificateData.intervention_project_period)}
            </Text>

            <View
              style={{
                marginTop: 12,
                padding: 10,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  fontFamily: "Lexend",
                  fontWeight: 500,
                  color: "#35B28D",
                  marginBottom: 6,
                }}
              >
                Methodologies Applied:
              </Text>
              {Array.isArray(certificateData.intervention_method) &&
                certificateData.intervention_method.length > 0 ? (
                certificateData.intervention_method.map((method, idx) => (
                  <Text
                    key={idx}
                    style={{ fontSize: 9, marginLeft: 8, marginBottom: 3 }}
                  >
                    • {method.id}: {method.name}
                  </Text>
                ))
              ) : typeof certificateData.intervention_method === "string" ? (
                certificateData.intervention_method
                  .split(", ")
                  .map((method, idx) => (
                    <Text
                      key={idx}
                      style={{ fontSize: 9, marginLeft: 8, marginBottom: 3 }}
                    >
                      • {method}
                    </Text>
                  ))
              ) : (
                <Text style={{ fontSize: 9, marginLeft: 8 }}>-</Text>
              )}
            </View>
          </View>
        </View>

        {certificateData.emission_reduction_overview && (
          <View style={styles.pageBreak} wrap={false}>
            <Text style={styles.sectionTitle} minPresenceAhead={200}>
              Emission Reduction Overview
            </Text>
            <View style={styles.table} wrap={false} minPresenceAhead={50}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <View style={styles.tableCellSmall}>
                  <Text>Baseline</Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Project</Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Reduction</Text>
                </View>
                <Text style={styles.tableCellSmall}>Reduction (%)</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.baseline != null
                    ? Number(
                      certificateData.emission_reduction_overview.baseline
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.project != null
                    ? Number(
                      certificateData.emission_reduction_overview.project
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.delta != null
                    ? Number(
                      certificateData.emission_reduction_overview.delta
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview
                    .reduction_percent != null
                    ? `${Number(
                      certificateData.emission_reduction_overview
                        .reduction_percent
                    ).toFixed(1)} %`
                    : "-"}
                </Text>
              </View>
            </View>

            <Text
              style={[styles.text, { marginTop: 8 }]}
              minPresenceAhead={60}
            >
              <Text style={styles.bold}>Emission Intensity</Text>
            </Text>
            <View style={styles.table} wrap={false} minPresenceAhead={50}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <View style={styles.tableCellSmall}>
                  <Text>Baseline</Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Project</Text>
                </View>
                <View style={styles.tableCellSmall}>
                  <Text>Reduction</Text>
                </View>
                <Text style={styles.tableCellSmall}>Reduction (%)</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.emission_intensity
                    ?.baseline != null
                    ? Number(
                      certificateData.emission_reduction_overview.emission_intensity
                        .baseline
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.emission_intensity
                    ?.project != null
                    ? Number(
                      certificateData.emission_reduction_overview.emission_intensity
                        .project
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.emission_intensity
                    ?.reduction != null
                    ? Number(
                      certificateData.emission_reduction_overview.emission_intensity
                        .reduction
                    ).toFixed(3)
                    : "-"}
                </Text>
                <Text style={styles.tableCellSmall}>
                  {certificateData.emission_reduction_overview.emission_intensity
                    ?.baseline != null &&
                    certificateData.emission_reduction_overview.emission_intensity
                      ?.reduction != null &&
                    certificateData.emission_reduction_overview.emission_intensity.baseline !==
                    0
                    ? `${Number(
                      (certificateData.emission_reduction_overview.emission_intensity
                        .reduction /
                        certificateData.emission_reduction_overview.emission_intensity
                          .baseline) *
                      100
                    ).toFixed(1)} %`
                    : "-"}
                </Text>
              </View>
            </View>
          </View>
        )}

        {certificateData.emission_breakdown && (
          <>
            <View style={styles.pageBreak}>
              <View wrap={false}>
                <Text style={styles.sectionTitle} minPresenceAhead={300}>
                  Emission Breakdown
                </Text>

                <View wrap={false}>
                  <Text style={styles.text} minPresenceAhead={60}>
                    <Text style={styles.bold}>By Fertilizer Emission Type</Text>
                  </Text>
                  <View style={styles.table} wrap={false} minPresenceAhead={50}>
                    <View style={[styles.tableRow, styles.tableHeader]}>
                      <Text style={styles.tableCell}>Component</Text>
                      <View style={styles.tableCellSmall}>
                        <Text>Baseline</Text>
                      </View>
                      <View style={styles.tableCellSmall}>
                        <Text>Project</Text>
                      </View>
                      <View style={styles.tableCellSmall}>
                        <Text>Emission Reduction</Text>
                      </View>
                      <View style={styles.tableCellSmall}>
                        <Text>Reduction Intensity</Text>
                      </View>
                    </View>
                    {certificateData.emission_breakdown.byEmissionType.map(
                      (row, idx) => (
                        <View key={idx} style={styles.tableRow}>
                          <Text
                            style={[
                              styles.tableCell,
                              row.component === "Total" ? styles.bold : {},
                            ]}
                          >
                            {row.component}
                          </Text>
                          <Text style={styles.tableCellSmall}>
                            {row.baseline != null ? Number(row.baseline).toFixed(3) : "-"}
                          </Text>
                          <Text style={styles.tableCellSmall}>
                            {row.project != null ? Number(row.project).toFixed(3) : "-"}
                          </Text>
                          <Text
                            style={[
                              styles.tableCellSmall,
                              row.component === "Total" ? styles.bold : {},
                            ]}
                          >
                            {row.delta != null ? Number(row.delta).toFixed(3) : "-"}
                          </Text>
                          <Text
                            style={[
                              styles.tableCellSmall,
                              row.component === "Total" ? styles.bold : {},
                            ]}
                          >
                            {row.deltaPerTon != null ? Number(row.deltaPerTon).toFixed(3) : "-"}
                          </Text>
                        </View>
                      )
                    )}
                  </View>
                </View>
              </View>

              <View wrap={false}>
                <Text style={styles.text} minPresenceAhead={60}>
                  <Text style={styles.bold}>By Input Type</Text>
                </Text>
                <View style={styles.table} wrap={false} minPresenceAhead={50}>
                  <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.tableCell}>Input Type</Text>
                    <View style={styles.tableCellSmall}>
                      <Text>Baseline</Text>
                    </View>
                    <View style={styles.tableCellSmall}>
                      <Text>Project</Text>
                    </View>
                    <View style={styles.tableCellSmall}>
                      <Text>Emission Reduction</Text>
                    </View>
                    <View style={styles.tableCellSmall}>
                      <Text>Reduction Intensity</Text>
                    </View>
                  </View>
                  {certificateData.emission_breakdown.byInputType.map(
                    (row, idx) => {
                      const isTotalRow = row.inputType === "Total";
                      const isFertilizerWithInhibitor =
                        row.inputType === "Fertilizers" && row.includesInhibitor;
                      const label = isFertilizerWithInhibitor
                        ? "Fertilizers (+ Inhibitor)"
                        : row.inputType;

                      return (
                        <View key={idx} style={styles.tableRow}>
                          <Text
                            style={[
                              styles.tableCell,
                              isTotalRow ? styles.bold : {},
                            ]}
                          >
                            {label}
                          </Text>
                          <Text style={styles.tableCellSmall}>
                            {row.baseline != null ? Number(row.baseline).toFixed(3) : "-"}
                          </Text>
                          <Text style={styles.tableCellSmall}>
                            {row.project != null ? Number(row.project).toFixed(3) : "-"}
                          </Text>
                          <Text
                            style={[
                              styles.tableCellSmall,
                              isTotalRow ? styles.bold : {},
                            ]}
                          >
                            {row.delta != null ? Number(row.delta).toFixed(3) : "-"}
                          </Text>
                          <Text
                            style={[
                              styles.tableCellSmall,
                              isTotalRow ? styles.bold : {},
                            ]}
                          >
                            {row.deltaPerTon != null ? Number(row.deltaPerTon).toFixed(3) : "-"}
                          </Text>
                        </View>
                      );
                    }
                  )}
                </View>
              </View>
            </View>
          </>
        )}

        <View style={styles.pageBreak}>
          <Text style={styles.sectionTitle}>Governance and Assurance</Text>
          <Text style={styles.text}>
            <Text style={styles.bold}>Project Developer:</Text> 
            {certificateData.intervention_operator || "-"}
          </Text>
          {certificateData.intervention_project_sponsor && (
            <Text style={styles.text}>
              <Text style={styles.bold}>Project Sponsor:</Text> 
              {certificateData.intervention_project_sponsor}
            </Text>
          )}
          <Text style={styles.text}>
            <Text style={styles.bold}>Validator:</Text> 
            {certificateData.intervention_validator_of_quality || "-"}
          </Text>
          <Text style={styles.text}>
            <Text style={styles.bold}>Verifier:</Text> 
            {certificateData.intervention_verifier_of_quality || "-"}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
